import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { setCachedSchoolName } from '../utils/branding';
import { useSchoolDays } from '../hooks/useSchoolDays';

const TIMEZONES = [
  { value: 'Asia/Karachi', label: 'Karachi, Pakistan (PKT, UTC+5)' },
  { value: 'Asia/Lahore', label: 'Lahore, Pakistan (PKT, UTC+5)' },
  { value: 'Asia/Dubai', label: 'Dubai, UAE (GST, UTC+4)' },
  { value: 'Asia/Riyadh', label: 'Riyadh, Saudi Arabia (AST, UTC+3)' },
  { value: 'Asia/Kolkata', label: 'Kolkata, India (IST, UTC+5:30)' },
  { value: 'Asia/Dhaka', label: 'Dhaka, Bangladesh (BST, UTC+6)' },
  { value: 'Europe/London', label: 'London, UK' },
  { value: 'America/New_York', label: 'New York, USA' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

const MAX_SCHOOL_NAME_LENGTH = 100;


// ═══════════════════════════════════════════════════════════════════
// School Name card
// ═══════════════════════════════════════════════════════════════════
function SchoolNameCard() {
  const { user } = useAuth();
  const canEdit = ['admin', 'superintendent'].includes(user?.role);

  const [name, setName] = useState('');
  const [original, setOriginal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setLoading(true);
    api.get('/admin-settings/school-name')
      .then(r => {
        setName(r.data.school_name || '');
        setOriginal(r.data.school_name || '');
      })
      .catch(err => {
        setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setMessage({ type: 'error', text: 'School name cannot be empty.' });
      return;
    }
    if (trimmed.length > MAX_SCHOOL_NAME_LENGTH) {
      setMessage({ type: 'error', text: `Name must be ${MAX_SCHOOL_NAME_LENGTH} characters or less.` });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.put('/admin-settings/school-name', { school_name: trimmed });
      setName(res.data.school_name);
      setOriginal(res.data.school_name);

      // Notify the sidebar and cache the new name
      setCachedSchoolName(res.data.school_name);

      setMessage({ type: 'success', text: '✓ School name updated. It will appear on the login page immediately.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const changed = name.trim() !== original;
  const remaining = MAX_SCHOOL_NAME_LENGTH - name.length;

  if (loading) {
    return (
      <div className="bg-white p-6 rounded shadow mb-4 text-gray-500 text-sm">
        Loading school name…
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded shadow mb-4 border-l-4 border-blue-500">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <i className="fas fa-school text-blue-600"></i>
        School Name
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Shown on the login page, dashboard header, and printed documents.
        {!canEdit && (
          <span className="ml-1 text-amber-700">
            Only admin and superintendent can change this.
          </span>
        )}
      </p>

      <label className="block text-xs font-medium text-gray-600 mb-1">Display name</label>
      <input
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        maxLength={MAX_SCHOOL_NAME_LENGTH}
        disabled={!canEdit}
        placeholder="e.g., Greenwood High School"
        className={`w-full p-2.5 border rounded-md outline-none transition ${
          canEdit
            ? 'bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            : 'bg-gray-100 text-gray-500 cursor-not-allowed'
        }`}
      />

      <div className="flex items-center justify-between mt-1.5">
        <p className="text-xs text-gray-400">
          {canEdit ? `${remaining} characters remaining` : 'Contact admin to change'}
        </p>
      </div>

      {message.text && (
        <div className={`mt-3 p-3 rounded text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {canEdit && (
        <button
          onClick={handleSave}
          disabled={saving || !changed || !name.trim()}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
        >
          {saving ? (
            <><i className="fas fa-spinner fa-spin"></i> Saving…</>
          ) : (
            <><i className="fas fa-save"></i> Save School Name</>
          )}
        </button>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Attendance Rules card — work hours, late cutoff, half-day threshold
// ═══════════════════════════════════════════════════════════════════
function AttendanceRulesCard() {
  const { user } = useAuth();
  const canEdit = ['admin', 'superintendent'].includes(user?.role);

  const [form, setForm] = useState({
    work_start: '08:00',
    work_end: '15:00',
    late_after: '08:15',
    halfday_hours: 4,
  });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setLoading(true);
    api.get('/admin-settings/work-hours')
      .then(r => {
        const d = r.data;
        setForm({
          work_start: d.work_start || '08:00',
          work_end: d.work_end || '15:00',
          late_after: d.late_after || '08:15',
          halfday_hours: d.halfday_hours ?? 4,
        });
        setOriginal({
          work_start: d.work_start || '08:00',
          work_end: d.work_end || '15:00',
          late_after: d.late_after || '08:15',
          halfday_hours: d.halfday_hours ?? 4,
        });
      })
      .catch(err => setMessage({
        type: 'error',
        text: err.response?.data?.detail || err.message,
      }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (form.late_after < form.work_start) {
      setMessage({ type: 'error', text: 'Late-after must be on or after work start.' });
      return;
    }
    if (form.work_end <= form.work_start) {
      setMessage({ type: 'error', text: 'Work end must be after work start.' });
      return;
    }
    if (!(form.halfday_hours > 0 && form.halfday_hours <= 12)) {
      setMessage({ type: 'error', text: 'Half-day hours must be between 0 and 12.' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.put('/admin-settings/work-hours', {
        work_start: form.work_start,
        work_end: form.work_end,
        late_after: form.late_after,
        halfday_hours: parseFloat(form.halfday_hours),
      });
      setForm({
        work_start: res.data.work_start,
        work_end: res.data.work_end,
        late_after: res.data.late_after,
        halfday_hours: res.data.halfday_hours,
      });
      setOriginal({
        work_start: res.data.work_start,
        work_end: res.data.work_end,
        late_after: res.data.late_after,
        halfday_hours: res.data.halfday_hours,
      });
      setMessage({ type: 'success', text: '✓ Attendance rules updated.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const changed = original && (
    form.work_start !== original.work_start ||
    form.work_end !== original.work_end ||
    form.late_after !== original.late_after ||
    parseFloat(form.halfday_hours) !== original.halfday_hours
  );

  if (loading) {
    return (
      <div className="bg-white p-6 rounded shadow mb-4 text-gray-500 text-sm">
        Loading attendance rules…
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded shadow mb-4 border-l-4 border-emerald-500">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <i className="fas fa-clock text-emerald-600"></i>
        Attendance Rules
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Defines when a staff check-in is marked <strong>Late</strong>, and the
        minimum hours needed for a full day (below this = <strong>Half-Day</strong>).
        {!canEdit && (
          <span className="ml-1 text-amber-700">
            Only admin and superintendent can change these.
          </span>
        )}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Work Start Time
          </label>
          <input
            type="time"
            value={form.work_start}
            onChange={e => setForm({ ...form, work_start: e.target.value })}
            disabled={!canEdit}
            className={`w-full p-2.5 border rounded-md outline-none transition ${
              canEdit
                ? 'bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
            }`}
          />
          <p className="text-[11px] text-gray-400 mt-1">Official start of the school day</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Work End Time
          </label>
          <input
            type="time"
            value={form.work_end}
            onChange={e => setForm({ ...form, work_end: e.target.value })}
            disabled={!canEdit}
            className={`w-full p-2.5 border rounded-md outline-none transition ${
              canEdit
                ? 'bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
            }`}
          />
          <p className="text-[11px] text-gray-400 mt-1">Used as reference only (not enforced)</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Late After <span className="text-red-500">*</span>
          </label>
          <input
            type="time"
            value={form.late_after}
            onChange={e => setForm({ ...form, late_after: e.target.value })}
            disabled={!canEdit}
            className={`w-full p-2.5 border rounded-md outline-none transition ${
              canEdit
                ? 'bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
                : 'bg-gray-100 text-gray-500 cursor-not-allowed'
            }`}
          />
          <p className="text-[11px] text-red-600 mt-1">
            Anyone checking in after this → <strong>Late</strong>
          </p>
        </div>
      </div>

      <div className="mb-2 max-w-xs">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Half-Day Below (hours) <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          min="0.5"
          max="12"
          step="0.5"
          value={form.halfday_hours}
          onChange={e => setForm({ ...form, halfday_hours: e.target.value })}
          disabled={!canEdit}
          className={`w-full p-2.5 border rounded-md outline-none transition ${
            canEdit
              ? 'bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500'
              : 'bg-gray-100 text-gray-500 cursor-not-allowed'
          }`}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          If total worked hours &lt; this → <strong>Half-Day</strong>
        </p>
      </div>

      <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
        <i className="fas fa-info-circle mr-1"></i>
        <strong>Current behaviour:</strong> check-in after <strong>{form.late_after}</strong> → <em>Late</em>;
        worked hours under <strong>{form.halfday_hours}h</strong> → <em>Half-Day</em>.
      </div>

      {message.text && (
        <div className={`mt-3 p-3 rounded text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {canEdit && (
        <button
          onClick={handleSave}
          disabled={saving || !changed}
          className="mt-4 bg-emerald-600 text-white px-6 py-2 rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
        >
          {saving ? (
            <><i className="fas fa-spinner fa-spin"></i> Saving…</>
          ) : (
            <><i className="fas fa-save"></i> Save Attendance Rules</>
          )}
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Session Timeout card
// ══════════════════════════════════════════════════════════
function SessionTimeoutCard() {
  const { user } = useAuth();
  const canEdit = ['admin'].includes(user?.role);

  const PRESETS = [5, 15, 30, 60, 120];
  const DEFAULT_MINUTES = 30;

  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [original, setOriginal] = useState(DEFAULT_MINUTES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    api.get('/admin-settings/session-timeout')
      .then(r => {
        const m = r.data.session_timeout_minutes || DEFAULT_MINUTES;
        setMinutes(m);
        setOriginal(m);
      })
      .catch(err => setMessage({ type: 'error', text: err.response?.data?.detail || err.message }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (minutes < 1 || minutes > 1440) {
      setMessage({ type: 'error', text: 'Timeout must be between 1 and 1440 minutes.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.put('/admin-settings/session-timeout', {
        session_timeout_minutes: minutes,
      });
      const m = res.data.session_timeout_minutes;
      setMinutes(m);
      setOriginal(m);
      setMessage({ type: 'success', text: `✅ Session timeout set to ${m} minute${m !== 1 ? 's' : ''}` });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const changed = minutes !== original;
  const human = (m) => {
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}h ${r}m` : `${h}h`;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded shadow mb-4 text-gray-500 text-sm">
        Loading session settings…
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded shadow mb-4 border-l-4 border-orange-500">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <i className="fas fa-shield-alt text-orange-600"></i>
        Session Timeout
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Auto-logout users after this many minutes of inactivity.
        A 60-second warning appears before logout, letting users stay logged in with one click.
        {!canEdit && <span className="ml-1 text-amber-700">Only admin can change this.</span>}
      </p>

      {/* Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {PRESETS.map(p => {
          const isActive = minutes === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => canEdit && setMinutes(p)}
              disabled={!canEdit}
              className={`px-3 py-1.5 text-xs rounded border font-medium transition ${
                isActive
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
              } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {p} min
            </button>
          );
        })}
      </div>

      {/* Custom */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="text-sm font-medium text-gray-700">Custom:</label>
        <input
          type="number"
          min="1"
          max="1440"
          value={minutes}
          disabled={!canEdit}
          onChange={e => setMinutes(parseInt(e.target.value) || 1)}
          className={`p-2 border rounded w-28 text-sm ${
            canEdit ? 'bg-white' : 'bg-gray-100 text-gray-500 cursor-not-allowed'
          }`}
        />
        <span className="text-sm text-gray-500">minutes (1–1440)</span>
      </div>

      <div className="text-xs text-gray-600 bg-orange-50 border border-orange-200 rounded p-2 mb-3">
        <i className="fas fa-info-circle mr-1 text-orange-600"></i>
        Currently: <strong>{human(original)}</strong> idle timeout
        {original > 60 && <span className="text-gray-500"> ({original} minutes)</span>}
      </div>

      {message.text && (
        <div className={`mb-3 p-3 rounded text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {canEdit && (
        <button
          onClick={handleSave}
          disabled={saving || !changed}
          className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
        >
          {saving ? (
            <><i className="fas fa-spinner fa-spin"></i> Saving…</>
          ) : (
            <><i className="fas fa-save"></i> Save Session Timeout</>
          )}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Admin Settings page
// ═══════════════════════════════════════════════════════════════════
export default function AdminSettings() {
  const [timezone, setTimezone] = useState('Asia/Karachi');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin-settings/timezone');
      setTimezone(res.data.timezone);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/admin-settings/timezone', { timezone });
      setMessage(`✓ Timezone updated to ${res.data.timezone}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('✗ ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const current = TIMEZONES.find(t => t.value === timezone);
  const localTime = new Date().toLocaleTimeString('en-PK', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  });

  if (loading) return <p className="text-gray-500">Loading…</p>;

  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-bold mb-4">Admin Settings</h2>

      {message && (
        <div className={`mb-4 p-3 rounded ${
          message.startsWith('✓')
            ? 'bg-green-50 text-green-800'
            : 'bg-red-50 text-red-800'
        }`}>
          {message}
        </div>
      )}

      <SchoolNameCard />
      <SchoolDaysCard />
      <AttendanceRulesCard />
      <SessionTimeoutCard />

      <div className="bg-white p-6 rounded shadow mb-4">
        <h3 className="text-lg font-semibold mb-1">
          <i className="fas fa-globe text-blue-500 mr-2"></i>
          System Timezone
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          All check-in / check-out times are stored in UTC and displayed in this timezone.
          Only admins can change this.
        </p>

        <label className="block text-sm font-medium mb-2">Timezone</label>
        <select
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
          className="w-full p-2 border rounded mb-3"
        >
          {TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>

        <div className="flex items-center justify-between bg-blue-50 p-3 rounded text-sm">
          <span className="text-blue-800">
            <i className="fas fa-clock mr-1"></i>
            Current time in <strong>{current?.label?.split(',')[0] || timezone}</strong>:
          </span>
          <span className="font-mono font-bold text-blue-900">{localTime}</span>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Timezone'}
        </button>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded text-sm text-yellow-800">
        <i className="fas fa-info-circle mr-1"></i>
        <strong>Note:</strong> Only admins can edit check-in/out times manually.
        If a biometric device is connected, its clock should be set to this timezone too.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// School Days card — which days of the week count as school days
// ══════════════════════════════════════════════════════════
function SchoolDaysCard() {
  const { user } = useAuth();
  const canEdit = ['admin'].includes(user?.role);

  const DAYS = [
    { num: 0, name: 'Sunday',    short: 'Sun' },
    { num: 1, name: 'Monday',    short: 'Mon' },
    { num: 2, name: 'Tuesday',   short: 'Tue' },
    { num: 3, name: 'Wednesday', short: 'Wed' },
    { num: 4, name: 'Thursday',  short: 'Thu' },
    { num: 5, name: 'Friday',    short: 'Fri' },
    { num: 6, name: 'Saturday',  short: 'Sat' },
  ];

  const PRESETS = [
    { label: 'Mon–Fri (5 days)', days: [1, 2, 3, 4, 5] },
    { label: 'Mon–Sat (6 days)', days: [1, 2, 3, 4, 5, 6] },
    { label: 'Sun–Thu (5 days)', days: [0, 1, 2, 3, 4] },
  ];

  const [selected, setSelected] = useState([1, 2, 3, 4, 5]);
  const [original, setOriginal] = useState([1, 2, 3, 4, 5]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setLoading(true);
    api.get('/admin-settings/school-days')
      .then(r => {
        const days = r.data?.school_days || [1, 2, 3, 4, 5];
        setSelected(days);
        setOriginal(days);
      })
      .catch(err => {
        setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleDay = (num) => {
    if (!canEdit) return;
    setSelected(prev => {
      if (prev.includes(num)) {
        const next = prev.filter(d => d !== num);
        return next.length === 0 ? prev : next;
      }
      return [...prev, num].sort((a, b) => a - b);
    });
  };

  const applyPreset = (days) => {
    if (!canEdit) return;
    setSelected([...days]);
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one day.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.put('/admin-settings/school-days', {
        school_days: selected,
      });
      const days = res.data.school_days || selected;
      setSelected(days);
      setOriginal(days);
      setMessage({ type: 'success', text: '✅ School days updated. Reload pages to see the change.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const changed = JSON.stringify([...selected].sort()) !== JSON.stringify([...original].sort());

  if (loading) {
    return (
      <div className="bg-white p-6 rounded shadow mb-4 text-gray-500 text-sm">
        Loading school days…
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded shadow mb-4 border-l-4 border-indigo-500">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <i className="fas fa-calendar-week text-indigo-600"></i>
        School Days
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Which days of the week count as school days. This affects attendance marking,
        timetables, and daily reports.
        {!canEdit && <span className="ml-1 text-amber-700">Only admin can change this.</span>}
      </p>

      {/* Presets */}
      {canEdit && (
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map(p => {
            const isActive = JSON.stringify([...p.days].sort()) === JSON.stringify([...selected].sort());
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.days)}
                className={`px-3 py-1.5 text-xs rounded border transition font-medium ${
                  isActive
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Day toggles */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {DAYS.map(d => {
          const isOn = selected.includes(d.num);
          return (
            <button
              key={d.num}
              type="button"
              onClick={() => toggleDay(d.num)}
              disabled={!canEdit}
              className={`p-3 rounded-lg border-2 transition text-center ${
                isOn
                  ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                  : 'bg-white border-gray-200 text-gray-400 hover:border-gray-400'
              } ${!canEdit ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
              title={d.name}
            >
              <p className="text-xs font-bold uppercase">{d.short}</p>
              <p className="text-[10px] mt-1">
                {isOn ? <i className="fas fa-check-circle"></i> : 'Off'}
              </p>
            </button>
          );
        })}
      </div>

      <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 mb-3">
        <i className="fas fa-info-circle mr-1"></i>
        Currently: <strong>{selected.length} day{selected.length !== 1 ? 's' : ''} per week</strong>
        {' — '}
        {selected.map(n => DAYS.find(d => d.num === n)?.short).join(', ')}
      </div>

      {message.text && (
        <div className={`mt-3 p-3 rounded text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {canEdit && (
        <button
          onClick={handleSave}
          disabled={saving || !changed}
          className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
        >
          {saving ? (
            <><i className="fas fa-spinner fa-spin"></i> Saving…</>
          ) : (
            <><i className="fas fa-save"></i> Save School Days</>
          )}
        </button>
      )}
    </div>
  );
}